import React, {useEffect, useState} from 'react';
import {GridColDef, GridRowParams} from "@mui/x-data-grid";
import {Filter} from "../../shared/types/filter";
import {UrlFilterAwareDataGrid} from "../../shared/components/UrlFilterAwareDataGrid";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Select,
  SelectChangeEvent,
  Tooltip
} from "@mui/material";
import {enqueueSnackbar} from "notistack";
import {getMatches, deleteMatch, restoreMatch} from "../../services/match.service";
import MatchInfoComponent from "./MatchInfoComponent";
import {useNavigate, useSearchParams} from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { MatchInfoDto } from 'sandy-shared/dist/dtos/match-info.dto';
import {ALL_SEASONS, CURRENT_SEASON, Season} from "sandy-shared/dist/types/season-info.type";

const activeFilter: Filter<MatchInfoDto> = {
  displayName: 'Active',
  urlParam: 'active',
  filterFunction: (values: MatchInfoDto[]) => values.filter(match => !match.deletedAt)
}
const deletedFilter: Filter<MatchInfoDto> = {
  displayName: 'Deleted',
  urlParam: 'deleted',
  filterFunction: (values: MatchInfoDto[]) => values.filter(match => !!match.deletedAt)
}
const allFilters = [activeFilter, deletedFilter];
const defaultFilter = activeFilter;

const SEASON_PARAM = 'season';
const ALL_SEASONS_OPTION = 'all';

const matchRowBuilder = (match: MatchInfoDto) => {
  const scores = match.sets.map(set => `${set.homeScore}-${set.awayScore}`).join(', ');
  return {
    id: match.id,
    homePartnership: `${match.homePartnership.playerOne.lastName}/${match.homePartnership.playerTwo.lastName}`,
    awayPartnership: `${match.awayPartnership.playerOne.lastName}/${match.awayPartnership.playerTwo.lastName}`,
    scores,
    type: match.type,
    status: match.status,
    season: match.season,
    date: match.date,
    deletedAt: match.deletedAt
  }
}

const NEW_MATCH_ID = -1;

const MatchesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedMatchId, setSelectedMatchId] = useState<number | undefined>(undefined);
  const [matches, setMatches] = useState<MatchInfoDto[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [matchIdToDelete, setMatchIdToDelete] = useState<number | undefined>(undefined);

  // Season lives in the URL so a filtered view can be bookmarked/shared; defaults to the current season
  const seasonParam = searchParams.get(SEASON_PARAM);
  const selectedSeason: Season | typeof ALL_SEASONS_OPTION =
    seasonParam === ALL_SEASONS_OPTION ? ALL_SEASONS_OPTION
    : (ALL_SEASONS as string[]).includes(seasonParam ?? '') ? seasonParam as Season
    : CURRENT_SEASON;

  useEffect(() => {
    refreshMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason]);

  const refreshMatches = () => {
    setIsLoading(true);
    getMatches({
      season: selectedSeason === ALL_SEASONS_OPTION ? undefined : selectedSeason,
      includeDeleted: true // The Active/Deleted chips split them client-side
    })
      .then(matches => setMatches(matches))
      .catch(e => {
        console.error('Error getting matches', e)
        enqueueSnackbar(`Error getting matches: ${e}`, {variant: 'error', autoHideDuration: 5000})
      })
      .finally(() => setIsLoading(false));
  }

  const handleSeasonChange = (event: SelectChangeEvent) => {
    searchParams.set(SEASON_PARAM, event.target.value);
    setSearchParams(searchParams);
  }

  const handleRowClick = (params: GridRowParams) => {
    if (params.row.deletedAt) {
      enqueueSnackbar('This match is deleted. Restore it to open it.', {variant: 'info', autoHideDuration: 4000});
      return;
    }
    navigate(`/match-statting/${params.id}`);
  };

  const handleCloseMatchInfo = () => {
    setSelectedMatchId(undefined);
  }

  const handleSaveMatchInfo = () => {
    setSelectedMatchId(undefined);
    refreshMatches();
  }

  const handleAddNewMatchClick = () => {
    setSelectedMatchId(NEW_MATCH_ID);
  }

  const handleDeleteClick = (event: React.MouseEvent, matchId: number) => {
    event.stopPropagation();
    setMatchIdToDelete(matchId);
    setDeleteDialogOpen(true);
  }

  const handleConfirmDelete = () => {
    if (matchIdToDelete) {
      deleteMatch(matchIdToDelete)
        .then(() => {
          enqueueSnackbar('Match deleted. It can be restored from the Deleted view.', {variant: 'success', autoHideDuration: 5000});
          refreshMatches();
        })
        .catch(e => {
          console.error('Error deleting match', e);
          enqueueSnackbar(`Error deleting match: ${e}`, {variant: 'error', autoHideDuration: 5000});
        })
        .finally(() => {
          setDeleteDialogOpen(false);
          setMatchIdToDelete(undefined);
        });
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setMatchIdToDelete(undefined);
  }

  const handleRestoreClick = (event: React.MouseEvent, matchId: number) => {
    event.stopPropagation();
    restoreMatch(matchId)
      .then(() => {
        enqueueSnackbar('Match restored', {variant: 'success', autoHideDuration: 5000});
        refreshMatches();
      })
      .catch(e => {
        console.error('Error restoring match', e);
        enqueueSnackbar(`Error restoring match: ${e}`, {variant: 'error', autoHideDuration: 5000});
      });
  }

  const matchColumns: GridColDef[] = [
    { field: 'homePartnership', headerName: 'Home Partnership', width: 200 },
    { field: 'awayPartnership', headerName: 'Away Partnership', width: 200 },
    { field: 'scores', headerName: 'Scores', width: 250 },
    { field: 'type', headerName: 'Type', width: 100 },
    { field: 'status', headerName: 'Status', width: 100 },
    { field: 'season', headerName: 'Season', width: 120 },
    { field: 'date', headerName: 'Date', width: 250 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => params.row.deletedAt
        ? (
          <Tooltip title={`Deleted ${new Date(params.row.deletedAt).toLocaleString()}. Click to restore.`}>
            <IconButton color="primary" onClick={event => handleRestoreClick(event, params.id as number)}>
              <RestoreFromTrashIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton color="error" onClick={event => handleDeleteClick(event, params.id as number)}>
            <DeleteIcon />
          </IconButton>
        )
    }
  ];

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', padding: '25px'}}>
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, padding: 2}}>
        <FormControl sx={{minWidth: 180}} size="small">
          <InputLabel id="season-select-label">Season</InputLabel>
          <Select labelId="season-select-label" label="Season" value={selectedSeason} onChange={handleSeasonChange}>
            {[...ALL_SEASONS].reverse().map(season => (
              <MenuItem key={season} value={season}>{season}</MenuItem>
            ))}
            <MenuItem value={ALL_SEASONS_OPTION}>All seasons</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleAddNewMatchClick}>Create New Match</Button>
      </Box>
      <UrlFilterAwareDataGrid
        columns={matchColumns}
        rowBuilder={matchRowBuilder}
        data={matches}
        onRowClick={handleRowClick}
        allFilters={allFilters}
        defaultFilters={[defaultFilter]}
        loading={isLoading}
      />
      <Modal
        open={!!selectedMatchId}
        onClose={() => handleCloseMatchInfo()}
      >
        <MatchInfoComponent matchId={selectedMatchId === NEW_MATCH_ID ? undefined : selectedMatchId} onClose={() => handleCloseMatchInfo()} onSave={() => handleSaveMatchInfo()}/>
      </Modal>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this match? It will be hidden from the matches list, stats, and analysis, but can be restored later from the Deleted view.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} variant={'contained'} color="primary">Cancel</Button>
          <Button onClick={handleConfirmDelete} variant={'contained'} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
};

export default MatchesListPage;
